import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import StatusSelect from "react-select";
import Pagination from "../components/ui/pagination";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { api } from "../lib/utils";
import {
  Ticket,
  Clock,
  User,
  CheckCircle,
  Plus,
  Building2,
  Calendar,
  Edit3,
  Download,
} from "lucide-react";
import { format } from "date-fns";
import moment from "moment-timezone";
import { downloadFile } from "../lib/fileUpload";

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [allTickets, setAllTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [totalTickets, setTotalTickets] = useState(0);
  const [clearFilter, setClearFilter] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPgs: 1,
    totalCount: 0,
    limit: 10,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const ticketsPerPage = 10;
  const parsedUser = typeof user === "string" ? JSON.parse(user) : user;
  const [priorityFilter, setPriorityFilter] = useState("ALL_PRIORITY");
  const [departmentFilter, setDepartmentFilter] = useState(
    parsedUser?.Department === "Management"
      ? "ALL_DEPARTMENT"
      : parsedUser?.Department === "HR" || parsedUser?.Department === "HRD"
      ? "HR"
      : parsedUser?.Department === "Finance"
      ? "FINANCE"
      : parsedUser?.Department === "Administration"
      ? "ADMIN"
      : "",
  );
  const [adminAndFinAccessUsers, setAdminAndFinAccessUsers] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState([
    {
      value: "OPEN",
      label: "Open",
    },
    {
      value: "IN_PROGRESS",
      label: "In Progress",
    },
  ]);
  const options = [
    { value: "ALL_STATUS", label: "All Status" },
    { value: "OPEN", label: "Open" },
    { value: "IN_PROGRESS", label: "In Progress" },
    { value: "ON_HOLD", label: "On Hold" },
    { value: "CLOSED", label: "Closed" },
    { value: "CANCELLED", label: "Cancelled" },
  ];
  const [widgetSelected, setWidgetSelected] = useState("");
  const [params, setParams] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [byDefault, setByDefault] = useState([
    {
      value: "OPEN",
      label: "Open",
    },
    {
      value: "IN_PROGRESS",
      label: "In Progress",
    },
  ]);

  const slaStatus = (createdAt, sla) => {
    if (!createdAt || !sla) return;

    const createdTime = new Date(createdAt);

    let expiryTime = new Date(createdTime);

    if (sla.toLowerCase().includes("days")) {
      const days = parseInt(sla, 10); // "1 days" → 1
      expiryTime.setDate(expiryTime.getDate() + days);
    } else if (
      sla.toLowerCase().includes("hrs") ||
      sla.toLowerCase().includes("hours")
    ) {
      const hours = parseInt(sla, 10); // "4 hrs" → 4
      expiryTime.setHours(expiryTime.getHours() + hours);
    }

    const now = new Date();
    return now > expiryTime ? "bg-red-200" : "bg-white";
  };

  const department = () => {
    return parsedUser?.Department === "Management"
      ? "ALL_DEPARTMENT"
      : parsedUser?.Department === "HR" || parsedUser?.Department === "HRD"
      ? "HR"
      : parsedUser?.Department === "Finance"
      ? "FINANCE"
      : parsedUser?.Department === "Administration"
      ? "ADMIN"
      : "";
  };

  const customStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: "40px",
      height: "40px",
      overflow: "hidden",
      whiteSpace: "nowrap",
    }),
    valueContainer: (provided) => ({
      ...provided,
      display: "flex",
      flexWrap: "nowrap",
      overflowX: "auto",
      scrollbarWidth: "none", // Firefox
      msOverflowStyle: "none", // IE/Edge
    }),
    multiValue: (provided) => ({
      ...provided,
      maxWidth: "120px",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    }),
  };

  useEffect(() => {
    if (parsedUser?.Department !== "Management") {
      if (tickets.length) {
        setLoading(true);

        const itemsPerPage = 10;

        const currentPage = Math.max(
          1,
          Math.min(pagination.currentPage, totalPages),
        );

        setPagination({
          currentPage: currentPage,
          totalPgs: totalPages,
          totalCount: totalTickets,
          limit: itemsPerPage,
          hasNextPage: currentPage < totalPages,
          hasPrevPage: currentPage > 1,
        });

        setLoading(false);
      }
    }
  }, [tickets]);

  useEffect(() => {
    if (widgetSelected) {
      fetchDashboardData(widgetSelected, "");
    } else if (clearFilter === true) {
      fetchDashboardData("", [{ status: ["OPEN", "IN_PROGRESS"] }]);
    } else {
      fetchDashboardData("", params);
    }
  }, [pagination?.currentPage, params, widgetSelected, clearFilter]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    const newParams = {};

    const selectedStatus =
      selectedOptions.length > 0 && selectedOptions?.map((opt) => opt.value);
    if (selectedStatus.length > 0 && !selectedStatus.includes("ALL_STATUS")) {
      newParams.status = selectedStatus;
    }

    if (priorityFilter !== "ALL_PRIORITY") {
      newParams.priority = priorityFilter;
    }

    if (departmentFilter !== "ALL_DEPARTMENT") {
      newParams.department = departmentFilter;
    }

    setParams(newParams);
  }, [selectedOptions, priorityFilter, departmentFilter]);

  useEffect(() => {
    if (
      parsedUser?.Department === "Management" &&
      (selectedOptions.length ||
        priorityFilter !== "ALL_PRIORITY" ||
        departmentFilter !== "ALL_DEPARTMENT")
    ) {
      setWidgetSelected("");
    } else if (
      parsedUser?.Department !== "Management" &&
      (selectedOptions.length ||
        priorityFilter !== "ALL_PRIORITY" ||
        departmentFilter !==
          (parsedUser?.Department === "HR" || parsedUser?.Department === "HRD"
            ? "HR"
            : parsedUser?.Department === "Finance"
            ? "FINANCE"
            : parsedUser?.Department === "Administration"
            ? "ADMIN"
            : ""))
    ) {
      setWidgetSelected("");
    }
  }, [selectedOptions, priorityFilter, departmentFilter]);

  useEffect(() => {
    getAdminAndFinUsers();
  }, []);

  const getAdminAndFinUsers = async () => {
    try {
      const result = await api.get(`/api/categories/getAdminAnFinUsers`);

      if (result) {
        const emailIds = result?.data;
        const formattedEmailIds = emailIds.map((elem) => elem?.email);
        const bothAdminAndFinRoles = formattedEmailIds;
        setAdminAndFinAccessUsers(bothAdminAndFinRoles);
      } else {
        setAdminAndFinAccessUsers([]);
      }
    } catch (err) {
      return err;
    }
  };

  const queryParams = () => {
    const query = [];

    if (params.status) {
      params.status.forEach((status) => {
        query.push(`status=${encodeURIComponent(status)}`);
      });
    }

    if (params.priority) {
      query.push(`priority=${encodeURIComponent(params.priority)}`);
    }

    if (parsedUser?.Department === "Management" && params.department) {
      query.push(`department=${encodeURIComponent(params.department)}`);
    }

    if (
      parsedUser?.Department !== "Management" &&
      params.department &&
      !adminAndFinAccessUsers.includes(parsedUser?.email)
    ) {
      if (parsedUser?.Department === "Finance") {
        // Allow Finance users to filter by their selected department
        query.push(`department=${encodeURIComponent(params.department)}`);
      } else {
        query.push(
          `department=${encodeURIComponent(
            parsedUser?.Department === "HR" || parsedUser?.Department === "HRD"
              ? "HR"
              : parsedUser?.Department === "Administration"
              ? "ADMIN"
              : "",
          )}`,
        );
      }
    }
    if (
      parsedUser?.Department !== "Management" &&
      params.department &&
      adminAndFinAccessUsers.includes(parsedUser?.email)
    ) {
      query.push(
        `department=${encodeURIComponent(
          departmentFilter === "ADMIN" ? "ADMIN" : "FINANCE",
        )}`,
      );
    }

    return query.length ? `&${query.join("&")}` : "";
  };

  useEffect(() => {
    // if (parsedUser?.Department === "Management") {
    if (!allTickets.length) {
      setTickets([]);
      return;
    }

    // Client-side filtering
    let filteredTickets = allTickets;

    if (priorityFilter !== "ALL_PRIORITY" && !widgetSelected) {
      filteredTickets = filteredTickets.filter(
        (ticket) => ticket.priority === priorityFilter,
      );
    }

    if (departmentFilter !== "ALL_DEPARTMENT" && !widgetSelected) {
      filteredTickets = filteredTickets.filter(
        (ticket) => ticket.department === departmentFilter,
      );
    }
    const containsAllStatus =
      selectedOptions.length > 0 &&
      selectedOptions.map((elem) => elem?.value !== "ALL_STATUS");

    if (
      containsAllStatus &&
      containsAllStatus.includes(true) &&
      !widgetSelected
    ) {
      filteredTickets = filteredTickets.filter((ticket) =>
        selectedOptions.some((opt) => opt.value === ticket.status),
      );
    }

    setTickets(filteredTickets);
    // }
  }, [allTickets, selectedOptions, priorityFilter, departmentFilter]);

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, currentPage: newPage }));
  };

  const handleStatusWidgets = (type) => {
    setWidgetSelected(type);
    setSelectedOptions([]);
    setPriorityFilter("ALL_PRIORITY");
    setDepartmentFilter(
      parsedUser?.Department === "Management"
        ? "ALL_DEPARTMENT"
        : parsedUser?.Department === "HR" || parsedUser?.Department === "HRD"
        ? "HR"
        : parsedUser?.Department === "Finance"
        ? "FINANCE"
        : parsedUser?.Department === "Administration"
        ? "ADMIN"
        : "",
    );
    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
    }));
  };

  const fetchDashboardData = async (type, parameters) => {
    try {
      const [statsResponse, ticketsResponse] = await Promise.all([
        api.get("/api/dashboard/stats"),
        Object.entries(parameters).length !== 0
          ? api.get(
              `/api/tickets?page=${
                pagination?.currentPage
              }&limit=${ticketsPerPage}${
                queryParams()
                  ? queryParams()
                  : `&status=OPEN&status=IN_PROGRESS`
              }`,
            )
          : type !== "total_tickets"
          ? api.get(
              `/api/tickets?page=${pagination?.currentPage}&limit=${ticketsPerPage}&status=${type}`,
            )
          : api.get(
              `/api/tickets?page=${pagination?.currentPage}&limit=${ticketsPerPage}`,
            ),
      ]);

      setStats(statsResponse.stats);
      const now = new Date();

      const sortedTickets = [...ticketsResponse.tickets].sort((a, b) => {
        const isCrossedA = isSLACrossed(a, now);
        const isCrossedB = isSLACrossed(b, now);

        // SLA-crossed should come first
        if (isCrossedA && !isCrossedB) return -1;
        if (!isCrossedA && isCrossedB) return 1;

        // If both same SLA status, sort by createdAt desc
        return new Date(b.createdAt) - new Date(a.createdAt);
      });

      function isSLACrossed(ticket, now) {
        if (!ticket.sla) return false;

        const [valueStr, unit] = ticket.sla.split(" ");
        const value = parseInt(valueStr, 10);

        if (isNaN(value) || !unit) return false;

        let slaExpiry = new Date(ticket.createdAt);

        if (unit.toLowerCase().includes("day")) {
          slaExpiry.setDate(slaExpiry.getDate() + value);
        } else if (unit.toLowerCase().includes("hr")) {
          slaExpiry.setHours(slaExpiry.getHours() + value);
        }

        return now > slaExpiry;
      }

      setAllTickets(sortedTickets);
      // setAllTickets(ticketsResponse.tickets);
      // setTickets(ticketsResponse.tickets);
      setTickets(sortedTickets);
      setTotalPages(ticketsResponse?.pagination?.totalPages || 1);
      setTotalTickets(ticketsResponse?.pagination?.totalCount);

      if (parsedUser?.Department === "Management") {
        const itemsPerPage = 10;
        const totalFilteredCount = ticketsResponse?.pagination?.totalCount;

        // Ensure current page is within valid range
        const validPage = Math.max(
          1,
          Math.min(pagination.currentPage, totalPages),
        );

        setPagination({
          currentPage: validPage,
          totalPgs: ticketsResponse?.pagination?.totalPages,
          totalCount: totalFilteredCount,
          limit: itemsPerPage,
          hasNextPage: validPage < totalPages,
          hasPrevPage: validPage > 1,
        });
      }
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "OPEN":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "IN_PROGRESS":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "ON_HOLD":
        return "bg-purple-100 text-purple-800 border-purple-200";
      case "CLOSED":
        return "bg-green-100 text-green-800 border-green-200";
      case "CANCELLED":
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "HIGH":
        return "bg-red-100 text-red-800 border-red-200";
      case "MEDIUM":
        return "bg-orange-100 text-orange-800 border-orange-200";
      case "LOW":
        return "bg-green-100 text-green-800 border-green-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const handleTicketClick = (ticket) => {
    setSelectedTicket(ticket);
    setIsDetailModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsDetailModalOpen(false);
    setSelectedTicket(null);
  };

  const handleDownload = async (file) => {
    try {
      if (file.name || file.url || file.originalName) {
        await downloadFile(file.url, file.name, file.originalName);
      } else {
        console.error("No file key available for download");
      }
    } catch (error) {
      alert("Failed to download file: " + error.message);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="font-bold" style={{ fontSize: "1.285rem" }}>
          Dashboard
        </h1>
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="font-bold" style={{ fontSize: "1.285rem" }}>
              Dashboard
            </h1>
            <p className="text-gray-600">
              Welcome back, {parsedUser?.actort}! Here's your ticket overview.
            </p>
          </div>

          <div style={{ position: "relative" }}>
            <Button asChild>
              <Link to="/create-ticket">
                <Plus className="h-4 w-4 mr-2" />
                New Ticket
              </Link>
            </Button>
          </div>
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          <Card
            onClick={() => handleStatusWidgets("total_tickets")}
            className={`cursor-pointer ${
              widgetSelected === "total_tickets" ? "shadow-lg" : ""
            }`}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                TOTAL TICKETS
              </CardTitle>
              <Ticket className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {parsedUser?.Department === "Finance"
                  ? stats?.byDepartment?.admin -
                    stats?.byDepartment?.admin_cancelled_tickets +
                    (stats?.byDepartment?.finance -
                      stats?.byDepartment?.finance_cancelled)
                  : parsedUser?.Department === "Finance"
                  ? stats?.byDepartment?.finance -
                    stats?.byDepartment?.finance_cancelled
                  : parsedUser?.Department === "HR" ||
                    parsedUser?.Department === "HRD"
                  ? stats?.byDepartment?.hr - stats?.byDepartment?.hr_cancelled
                  : parsedUser?.Department === "Administration"
                  ? stats?.byDepartment?.admin -
                    stats?.byDepartment?.admin_cancelled_tickets
                  : stats?.total - stats?.cancelled}
              </div>
            </CardContent>
          </Card>

          <Card
            onClick={() => handleStatusWidgets("OPEN")}
            className={`cursor-pointer ${
              widgetSelected === "OPEN" ? getStatusColor("OPEN") : ""
            }`}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">OPEN</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {parsedUser?.Department === "Finance"
                  ? stats?.byDepartment?.admin_open_tickets +
                    stats?.byDepartment?.finance_open_tickets
                  : parsedUser?.Department === "Finance"
                  ? stats?.byDepartment?.finance_open_tickets
                  : parsedUser?.Department === "HR" ||
                    parsedUser?.Department === "HRD"
                  ? stats?.byDepartment?.hr_open
                  : parsedUser?.Department === "Administration"
                  ? stats?.byDepartment?.admin_open_tickets
                  : stats?.open}
              </div>
            </CardContent>
          </Card>

          <Card
            onClick={() => handleStatusWidgets("IN_PROGRESS")}
            className={`cursor-pointer ${
              widgetSelected === "IN_PROGRESS"
                ? getStatusColor("IN_PROGRESS")
                : ""
            }`}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">IN PROGRESS</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {parsedUser?.Department === "Finance"
                  ? stats?.byDepartment?.admin_in_progress_tickets +
                    stats?.byDepartment?.finance_in_progress_tickets
                  : parsedUser?.Department === "Finance"
                  ? stats?.byDepartment?.finance_in_progress_tickets
                  : parsedUser?.Department === "HR" ||
                    parsedUser?.Department === "HRD"
                  ? stats?.byDepartment?.hr_in_progress
                  : parsedUser?.Department === "Administration"
                  ? stats?.byDepartment?.admin_in_progress_tickets
                  : stats?.inProgress}
              </div>
            </CardContent>
          </Card>

          <Card
            onClick={() => handleStatusWidgets("ON_HOLD")}
            className={`cursor-pointer ${
              widgetSelected === "ON_HOLD" ? getStatusColor("ON_HOLD") : ""
            }`}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">ON HOLD</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {parsedUser?.Department === "Finance"
                  ? stats?.byDepartment?.admin_on_hold +
                    stats?.byDepartment?.finance_on_hold
                  : parsedUser?.Department === "Finance"
                  ? stats?.byDepartment?.finance_on_hold
                  : parsedUser?.Department === "HR" ||
                    parsedUser?.Department === "HRD"
                  ? stats?.byDepartment?.hr_on_hold
                  : parsedUser?.Department === "Administration"
                  ? stats?.byDepartment?.admin_on_hold
                  : stats?.onHold}
              </div>
            </CardContent>
          </Card>

          <Card
            onClick={() => handleStatusWidgets("CLOSED")}
            className={`cursor-pointer ${
              widgetSelected === "CLOSED" ? getStatusColor("CLOSED") : ""
            }`}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">CLOSED</CardTitle>
              <CheckCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {parsedUser?.Department === "Finance"
                  ? stats?.byDepartment?.finance_closed_tickets +
                    stats?.byDepartment?.admin_closed_tickets
                  : parsedUser?.Department === "Finance"
                  ? stats?.byDepartment?.finance_closed_tickets
                  : parsedUser?.Department === "HR" ||
                    parsedUser?.Department === "HRD"
                  ? stats?.byDepartment?.hr_closed
                  : parsedUser?.Department === "Administration"
                  ? stats?.byDepartment?.admin_closed_tickets
                  : stats?.closed}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* {parsedUser?.Department === "Management" && ( */}
        <div className="flex gap-4 items-end justify-between">
          <div className="flex gap-4 items-end">
            <div className="flex flex-col gap-2">
              <Label
                htmlFor="status-filter"
                className="text-sm font-medium font-poppins"
              >
                Status
              </Label>
              <StatusSelect
                isMulti
                options={options}
                value={selectedOptions}
                onChange={setSelectedOptions}
                className="w-[280px]"
                placeholder="All Status"
                styles={customStyles}
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label
                htmlFor="priority-filter"
                className="text-sm font-medium font-poppins"
              >
                Priority
              </Label>
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger
                  className="w-[180px] font-poppins"
                  id="priority-filter"
                >
                  <SelectValue placeholder="All Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL_PRIORITY">All Priority</SelectItem>
                  <SelectItem value="LOW">Low</SelectItem>
                  <SelectItem value="MEDIUM">Medium</SelectItem>
                  <SelectItem value="HIGH">High</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-2">
              <Label
                htmlFor="department-filter"
                className="text-sm font-medium font-poppins"
              >
                Department
              </Label>
              <div className="flex items-center gap-3">
                <Select
                  value={departmentFilter}
                  onValueChange={setDepartmentFilter}
                >
                  <SelectTrigger
                    className="w-[180px] font-poppins"
                    id="department-filter"
                  >
                    <SelectValue placeholder="All Department" />
                  </SelectTrigger>
                  <SelectContent>
                    {parsedUser?.Department === "Management" && (
                      <SelectItem value="ALL_DEPARTMENT">
                        All Department
                      </SelectItem>
                    )}

                    <SelectItem value="ADMIN">Infra</SelectItem>
                    <SelectItem value="HR">HR</SelectItem>
                    <SelectItem value="FINANCE">Finance</SelectItem>
                  </SelectContent>
                </Select>
                {(priorityFilter !== "ALL_PRIORITY" ||
                  JSON.stringify(selectedOptions) !==
                    JSON.stringify(byDefault) ||
                  (parsedUser?.Department === "Management"
                    ? departmentFilter !== "ALL_DEPARTMENT"
                    : parsedUser?.Department === "Administration"
                    ? departmentFilter !== "ADMIN"
                    : parsedUser?.Department === "Finance"
                    ? departmentFilter !== "FINANCE"
                    : parsedUser?.Department === "HR" ||
                      parsedUser?.Department === "HRD"
                    ? departmentFilter !== "HR"
                    : "")) && (
                  <button
                    onClick={() => {
                      setSelectedOptions([
                        {
                          value: "OPEN",
                          label: "Open",
                        },
                        {
                          value: "IN_PROGRESS",
                          label: "In Progress",
                        },
                      ]);

                      setPriorityFilter("ALL_PRIORITY");
                      setDepartmentFilter(
                        parsedUser?.Department === "Management"
                          ? "ALL_DEPARTMENT"
                          : parsedUser?.Department === "HR" ||
                            parsedUser?.Department === "HRD"
                          ? "HR"
                          : parsedUser?.Department === "Finance"
                          ? "FINANCE"
                          : parsedUser?.Department === "Administration"
                          ? "ADMIN"
                          : "",
                      );
                      setParams([{ status: ["OPEN", "IN_PROGRESS"] }]);
                      setClearFilter(true);
                    }}
                    className="text-blue-600 hover:text-blue-800 text-sm underline cursor-pointer font-poppins"
                  >
                    Clear Filter
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        {/* )} */}

        {/* Recent Tickets */}
        <div className="flex justify-end items-center">
          <div className="text-sm text-gray-500">
            {totalTickets > 0 ? (
              <>
                Showing {(pagination?.currentPage - 1) * ticketsPerPage + 1}-
                {Math.min(
                  pagination?.currentPage * ticketsPerPage,
                  totalTickets,
                )}{" "}
                of {totalTickets} tickets
              </>
            ) : (
              "No tickets found"
            )}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <div className="text-gray-500">Loading tickets...</div>
          </div>
        ) : tickets.length > 0 ? (
          tickets.map((ticket) => (
            <Card
              key={ticket.id}
              className={`hover:shadow-lg hover:border-blue-200 transition-all duration-200 cursor-pointer bg-white hover:bg-blue-50/30 ${slaStatus(
                ticket.createdAt,
                ticket.sla,
              )}`}
              onClick={() => handleTicketClick(ticket)}
            >
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <CardTitle className="text-base font-normal">
                      {ticket.department === "ADMIN"
                        ? "INFRA"
                        : ticket.department.slice(0, 3)}
                      #000{ticket.id} [{ticket.category} – {ticket.subcategory}]{" "}
                      {(() => {
                        let cleanSubject = ticket.subject;
                        const categoryPattern = ticket.category
                          ? ticket.category.replace(
                              /[.*+?^${}()|[\]\\]/g,
                              "\\$&",
                            )
                          : "";
                        const subcategoryPattern = ticket.subcategory
                          ? ticket.subcategory.replace(
                              /[.*+?^${}()|[\]\\]/g,
                              "\\$&",
                            )
                          : "";

                        if (categoryPattern && subcategoryPattern) {
                          const fullPattern = new RegExp(
                            `\\[?${categoryPattern}\\s*[–-]\\s*${subcategoryPattern}\\]?\\s*`,
                            "gi",
                          );
                          cleanSubject = cleanSubject
                            .replace(fullPattern, "")
                            .trim();
                        } else if (categoryPattern) {
                          const catPattern = new RegExp(
                            `\\[?${categoryPattern}\\]?\\s*`,
                            "gi",
                          );
                          cleanSubject = cleanSubject
                            .replace(catPattern, "")
                            .trim();
                        } else if (subcategoryPattern) {
                          const subPattern = new RegExp(
                            `\\[?${subcategoryPattern}\\]?\\s*`,
                            "gi",
                          );
                          cleanSubject = cleanSubject
                            .replace(subPattern, "")
                            .trim();
                        }

                        return cleanSubject;
                      })()}
                    </CardTitle>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <Badge className={getStatusColor(ticket.status)}>
                      {ticket.status.replace("_", " ")}
                    </Badge>
                    <Badge className={getPriorityColor(ticket.priority)}>
                      {ticket.priority}
                    </Badge>
                    {/* Edit icon for Open and Re-Open tickets */}
                    {ticket.status !== "CLOSED" && (
                      <Edit3
                        className="h-4 w-4 text-gray-600 hover:text-blue-600 cursor-pointer transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/edit-ticket/${ticket.id}`);
                        }}
                        title="Edit Ticket"
                      />
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex justify-between items-center text-sm text-gray-500">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1">
                      <Building2 className="h-4 w-4" />
                      {ticket.department === "ADMIN"
                        ? "INFRA"
                        : ticket.department.slice(0, 3)}
                    </div>
                    <div className="flex items-center gap-1">
                      <User className="h-4 w-4" />
                      Created By:
                      <span> {ticket?.user_name}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      Created:{" "}
                      {format(new Date(ticket.createdAt), "MMM d, yyyy")} -{" "}
                      <span>
                        {moment(ticket?.createdAt).format("hh:mm:ss A")}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <div className="text-center py-8">
            <div className="text-gray-500 mb-4">No tickets found</div>
            {department() === departmentFilter ? (
              <Button asChild>
                <Link to="/create-ticket">
                  <Plus className="h-4 w-4 mr-2" />
                  Create First Ticket
                </Link>
              </Button>
            ) : null}
          </div>
        )}

        <Dialog open={isDetailModalOpen} onOpenChange={setIsDetailModalOpen}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            {selectedTicket && (
              <>
                <DialogHeader>
                  <DialogTitle className="text-xl font-semibold">
                    {selectedTicket?.department === "ADMIN"
                      ? "INFRA"
                      : selectedTicket?.department.slice(0, 3)}
                    #000{selectedTicket.id} [{selectedTicket.category} –{" "}
                    {selectedTicket.subcategory}]{" "}
                    {(() => {
                      let cleanSubject = selectedTicket.subject;
                      // Remove any existing category/subcategory patterns from subject
                      const categoryPattern = selectedTicket.category
                        ? selectedTicket.category.replace(
                            /[.*+?^${}()|[\]\\]/g,
                            "\\$&",
                          )
                        : "";
                      const subcategoryPattern = selectedTicket.subcategory
                        ? selectedTicket.subcategory.replace(
                            /[.*+?^${}()|[\]\\]/g,
                            "\\$&",
                          )
                        : "";

                      if (categoryPattern && subcategoryPattern) {
                        const fullPattern = new RegExp(
                          `\\[?${categoryPattern}\\s*[–-]\\s*${subcategoryPattern}\\]?\\s*`,
                          "gi",
                        );
                        cleanSubject = cleanSubject
                          .replace(fullPattern, "")
                          .trim();
                      } else if (categoryPattern) {
                        const catPattern = new RegExp(
                          `\\[?${categoryPattern}\\]?\\s*`,
                          "gi",
                        );
                        cleanSubject = cleanSubject
                          .replace(catPattern, "")
                          .trim();
                      } else if (subcategoryPattern) {
                        const subPattern = new RegExp(
                          `\\[?${subcategoryPattern}\\]?\\s*`,
                          "gi",
                        );
                        cleanSubject = cleanSubject
                          .replace(subPattern, "")
                          .trim();
                      }

                      return cleanSubject;
                    })()}
                  </DialogTitle>
                  <DialogDescription>
                    Ticket details and information
                  </DialogDescription>
                  <div className="flex items-center gap-4 mt-2">
                    <Badge className={getStatusColor(selectedTicket.status)}>
                      {selectedTicket.status.replace("_", " ")}
                    </Badge>
                    <Badge
                      className={getPriorityColor(selectedTicket.priority)}
                    >
                      {selectedTicket.priority}
                    </Badge>
                    <span className="text-sm text-gray-500">
                      Created on{" "}
                      {format(
                        new Date(selectedTicket.createdAt),
                        "MMM d, yyyy 'at' h:mm a",
                      )}
                    </span>
                  </div>
                </DialogHeader>

                <div className="grid gap-6 py-4">
                  {/* Basic Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium border-b pb-2">
                      Ticket Information
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-medium text-gray-700">
                          Department
                        </Label>
                        <div className="mt-1 p-2 bg-gray-50 rounded border text-sm">
                          {selectedTicket?.department === "ADMIN"
                            ? "INFRA"
                            : selectedTicket?.department}
                        </div>
                      </div>

                      <div>
                        <Label className="text-sm font-medium text-gray-700">
                          Category
                        </Label>
                        <div className="mt-1 p-2 bg-gray-50 rounded border text-sm">
                          {selectedTicket?.category}
                        </div>
                      </div>

                      <div>
                        <Label className="text-sm font-medium text-gray-700">
                          Subcategory
                        </Label>
                        <div className="mt-1 p-2 bg-gray-50 rounded border text-sm">
                          {selectedTicket?.subcategory}
                        </div>
                      </div>

                      <div>
                        <Label className="text-sm font-medium text-gray-700">
                          Priority
                        </Label>
                        <div className="mt-1 p-2 bg-gray-50 rounded border text-sm">
                          <Badge
                            className={getPriorityColor(
                              selectedTicket?.priority,
                            )}
                          >
                            {selectedTicket?.priority}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <div className="space-y-2">
                    <h3 className="text-lg font-medium border-b pb-2">
                      Description
                    </h3>
                    <div className="p-3 bg-gray-50 rounded border text-sm whitespace-pre-wrap">
                      {selectedTicket?.description || "No description provided"}
                    </div>
                  </div>

                  {/* Comment */}
                  {selectedTicket?.comment && (
                    <div className="space-y-2">
                      <h3 className="text-lg font-medium border-b pb-2">
                        Additional Comments
                      </h3>
                      <div className="p-3 bg-gray-50 rounded border text-sm whitespace-pre-wrap">
                        {selectedTicket?.comment}
                      </div>
                    </div>
                  )}

                  {/* Files */}
                  {selectedTicket?.files &&
                    selectedTicket?.files.length > 0 && (
                      <div className="space-y-2">
                        <h3 className="text-lg font-medium border-b pb-2">
                          Attached Files
                        </h3>
                        <div className="space-y-2">
                          {typeof selectedTicket.files === "string"
                            ? JSON.parse(selectedTicket.files).map(
                                (file, index) => (
                                  <div
                                    key={file.id || index}
                                    className="flex items-center gap-3 p-3 bg-gray-50 rounded border"
                                  >
                                    <div className="flex items-center justify-between bg-gray-50 p-3 rounded-md">
                                      <p className="text-sm font-medium text-gray-900">
                                        {file.name || `File ${index + 1}`}
                                      </p>
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleDownload(file)}
                                        className="h-8 w-8 p-0"
                                        title="Download file"
                                      >
                                        <Download className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </div>
                                ),
                              )
                            : null}
                        </div>
                      </div>
                    )}

                  {/* Status and Timestamps */}
                  <div className="space-y-2">
                    <h3 className="text-lg font-medium border-b pb-2">
                      Status & Timeline
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-medium text-gray-700">
                          Current Status
                        </Label>
                        <div className="mt-1 p-2 bg-gray-50 rounded border text-sm">
                          <Badge
                            className={getStatusColor(selectedTicket.status)}
                          >
                            {selectedTicket.status.replace("_", " ")}
                          </Badge>
                        </div>
                      </div>

                      <div>
                        <Label className="text-sm font-medium text-gray-700">
                          Last Updated
                        </Label>
                        <div className="mt-1 p-2 bg-gray-50 rounded border text-sm">
                          {format(
                            new Date(selectedTicket.updatedAt),
                            "MMM d, yyyy 'at' h:mm a",
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t">
                  {(selectedTicket.status === "OPEN" ||
                    selectedTicket.status === "RE_OPEN") && (
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCloseModal();
                        navigate(`/edit-ticket/${selectedTicket.id}`);
                      }}
                      className="flex items-center gap-2"
                    >
                      <Edit3 className="h-4 w-4" />
                      Edit Ticket
                    </Button>
                  )}
                  <Button variant="outline" onClick={handleCloseModal}>
                    Close
                  </Button>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
      {totalPages > 1 && department() === departmentFilter && (
        <div className="mt-6">
          <Pagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPgs}
            totalCount={pagination.totalCount}
            limit={pagination.limit}
            hasNextPage={pagination.hasNextPage}
            hasPrevPage={pagination.hasPrevPage}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </>
  );
};

export default Dashboard;